import { useEffect, useRef } from "react";
import "./ProductSlider.css";
import HomeBarSub from "./HomeBarSub";
import { ArrowLeft, ArrowRight } from "lucide-react";
import FallbackImg from "../FallbackImg";

const AUTO_SCROLL_INTERVAL = 2000; // 2 seconds
const SCROLL_AMOUNT = 250;

const HomeBar = ({ products, renderViewMoreButton }) => {
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);
  const SHOW_ARROW_COUNT = 5;

  // Double the list — second copy creates the seamless loop illusion
  // Only double when there are more than 5 products; a single item should not be duplicated
  const extendedProducts =
    products.length > 5 ? [...products, ...products] : products;

  const scrollLeft = () => {
    sliderRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
  };

  const scrollRight = () => {
    sliderRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
  };

  // Seamless reset: when scroll enters the cloned second half, instantly
  // jump back to the identical position in the first half (no animation = invisible)
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      const halfWidth = slider.scrollWidth / 2;
      if (slider.scrollLeft >= halfWidth) {
        slider.scrollLeft -= halfWidth; // instant, no behavior:"smooth"
      }
    };

    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, [products]);

  // Auto-scroll effect (unchanged logic, just uses new scrollRight)
  useEffect(() => {
    intervalRef.current = setInterval(scrollRight, AUTO_SCROLL_INTERVAL);

    const slider = sliderRef.current;
    const stop = () => clearInterval(intervalRef.current);
    const start = () => {
      intervalRef.current = setInterval(scrollRight, AUTO_SCROLL_INTERVAL);
    };

    if (slider) {
      slider.addEventListener("mouseenter", stop);
      slider.addEventListener("mouseleave", start);
    }

    return () => {
      clearInterval(intervalRef.current);
      if (slider) {
        slider.removeEventListener("mouseenter", stop);
        slider.removeEventListener("mouseleave", start);
      }
    };
  }, []);

  return (
    <div className="homebar-section_v2">
      {/* header area (blue) */}
      <div className="homebar-header_v2">
        {renderViewMoreButton && renderViewMoreButton()}
      </div>

      {/* card area (white rounded box containing slider) */}
      <div className="homebar-card_v2">
        <div className="homebar_v2">
          {products.length > SHOW_ARROW_COUNT && (
            <button className="prev_v2" onClick={scrollLeft}>
              <ArrowLeft size={25} />
            </button>
          )}

          <div className="product-slider_v2" ref={sliderRef}>
            {/* Render doubled list — clones are visually identical */}
            {extendedProducts.map((product, index) => (
              <HomeBarSub product={product} key={index} />
            ))}
          </div>

          {products.length > SHOW_ARROW_COUNT && (
            <button className="next_v2" onClick={scrollRight}>
              <ArrowRight size={25} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeBar;
